import { OnboardingBotSetupContext } from "features/onboarding/guided/OnboardingBotSetup.context";
import useGridsSelector, {
  HEDGING_RANGE_SYMBOL_OVERRIDE_WARNING,
} from "@shared/features/bots/BotForm/General/useGridsSelector";
import { ErrorText, FormFieldTitle } from "features/bots/common/styles";
import CustomInputNumber from "common/Inputs/CustomInputNumber";
import React, { CSSProperties, ReactNode, useContext } from "react";
import Select from "common/Select/Select";
import Flex from "common/Boxes/Flex";
import Text from "common/Typography/Text";
import styled, { css, useTheme } from "styled-components";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { openMessage } from "utils/misc";
import { useIsOnboarding } from "utils/hooks/misc";
import useMeasure from "utils/hooks/useMeasure";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation.ts";
import { LanguageNameSpaces } from "@shared/types";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import { typography } from "@shared/common/Themes/typography";
import {
  HEDGING_RANGE_MAX_PRICE_INPUT_ID,
  HEDGING_RANGE_MIN_PRICE_INPUT_ID,
  HEDGING_RANGE_LOWER_LONG_INPUT_ID,
  HEDGING_RANGE_LOWER_SHORT_INPUT_ID,
  HEDGING_RANGE_UPPER_LONG_INPUT_ID,
  HEDGING_RANGE_UPPER_SHORT_INPUT_ID,
  HEDGING_STRATEGY_OVERRIDE_BUTTON_ID,
} from "testIds.ts";
import HedgingRatioSelector from "features/bots/BotForm/Entries/HedgingRatioSelector.tsx";
import { isSymbol } from "@shared/types/utils.ts";
import TitleWithToggle from "features/bots/BotForm/common/TitleWithToggle.tsx";
import { translateOptionName } from "@shared/common/i18n/optionNames";

interface GridsSelectorProps {
  style?: CSSProperties;
  updateGridsData?: boolean;
  isCopyPublicBotSummary?: boolean;
  isGeneralTab?: boolean;
  hideHedgingTitle?: boolean;
  afterPriceRangeContent?: ReactNode;
}

const GridsSelector = ({
  style,
  updateGridsData,
  isCopyPublicBotSummary,
  isGeneralTab,
  hideHedgingTitle,
  afterPriceRangeContent,
}: GridsSelectorProps) => {
  const { isNewLook } = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.BOTS);
  const isOnboarding = useIsOnboarding();
  const onboardingSetup = useContext(OnboardingBotSetupContext);
  const [hedgingExposureSectionRef, { width: hedgingExposureWidth }] =
    useMeasure<HTMLDivElement>();
  const {
    gridTypeSelectorData,
    numGridsSelectorData,
    priceRangeSelectorData,
    hedgingExposureSelectorData,
    rangeStrategySectionOverrideData,
    numGridsError,
    priceErrorMessage,
    exposureErrorMessage,
    generalGridsError,
  } = useGridsSelector({
    preserveExistingValues: !!onboardingSetup,
    updateFromStrategy: updateGridsData,
    openMessage,
    isOnboarding,
    isCopyPublicBotSummary,
  });

  const getPriceInput = (isMax?: boolean) => {
    const selectorData = isMax
      ? priceRangeSelectorData.max
      : priceRangeSelectorData.min;
    return (
      <div onClick={selectorData.onClick} style={{ flex: 1 }}>
        <CustomInputNumber
          data-testid={
            isMax
              ? HEDGING_RANGE_MAX_PRICE_INPUT_ID
              : HEDGING_RANGE_MIN_PRICE_INPUT_ID
          }
          onChange={selectorData.onChange}
          value={selectorData.value}
          placeholder={selectorData.placeholder}
          precision={priceRangeSelectorData.precision}
          disabled={selectorData.disabled}
          color={selectorData.color}
          stringMode
          useDebounce={true}
        />
      </div>
    );
  };

  const getPriceRangeSelector = () => {
    if (hedgingExposureSelectorData) {
      return (
        <div>
          <SectionLabel>
            {t("labels.price_range")} ({priceRangeSelectorData.percentChange}%)
          </SectionLabel>
          <PriceInputsWrapper>
            <PriceInputWrapper>
              <FormFieldTitle>{t("grids.min_price")}</FormFieldTitle>
              {getPriceInput()}
            </PriceInputWrapper>
            <PriceInputWrapper>
              <FormFieldTitle>{t("grids.max_price")}</FormFieldTitle>
              {getPriceInput(true)}
            </PriceInputWrapper>
          </PriceInputsWrapper>
        </div>
      );
    }
    return (
      <div>
        <FormFieldTitle>
          {t("labels.price_range")} ({priceRangeSelectorData.percentChange}%)
          {priceRangeSelectorData.helperData && (
            <HelperTooltip {...priceRangeSelectorData.helperData} />
          )}
        </FormFieldTitle>
        <Flex>
          {getPriceInput()}
          <Text type="secondary" style={{ padding: "0 6px" }}>
            {" - "}
          </Text>
          {getPriceInput(true)}
        </Flex>
      </div>
    );
  };

  const getHedgingExposureInput = (isUpper?: boolean) => {
    if (!hedgingExposureSelectorData) return;
    const selectorData = isUpper
      ? hedgingExposureSelectorData.upper
      : hedgingExposureSelectorData.lower;
    const boundaryLabel = isUpper
      ? t("labels.upper_boundary")
      : t("labels.lower_boundary");
    return (
      <HedgingExposureInput>
        <SectionLabel>{boundaryLabel}</SectionLabel>
        <HedgingRatioSelector
          longHedgingRatio={
            isSymbol(selectorData.value) ? undefined : selectorData.value
          }
          setLongHedgingRatio={(value) =>
            selectorData.onChange(value.toString())
          }
          isMultiValue={isSymbol(selectorData.value)}
          longLabel={`${t("labels.long")} %`}
          shortLabel={`${t("labels.short")} %`}
          longInputTestId={
            isUpper
              ? HEDGING_RANGE_UPPER_LONG_INPUT_ID
              : HEDGING_RANGE_LOWER_LONG_INPUT_ID
          }
          shortInputTestId={
            isUpper
              ? HEDGING_RANGE_UPPER_SHORT_INPUT_ID
              : HEDGING_RANGE_LOWER_SHORT_INPUT_ID
          }
          useFormFieldTitles={true}
        />
      </HedgingExposureInput>
    );
  };

  const getHedgingExposureSelector = () => {
    if (!hedgingExposureSelectorData) return;
    return (
      <HedgingExposureSection ref={hedgingExposureSectionRef}>
        {!hideHedgingTitle && (
          <FormFieldTitle>{t("labels.hedging")}</FormFieldTitle>
        )}
        <SectionDivider />
        <HedgingExposureWrapper $isInline={isHedgingExposureInline}>
          {getHedgingExposureInput()}
          <SectionDivider />
          {getHedgingExposureInput(true)}
        </HedgingExposureWrapper>
        {exposureErrorMessage && <ErrorText>{exposureErrorMessage}</ErrorText>}
      </HedgingExposureSection>
    );
  };

  const getGridTypeSelector = () => {
    if (!gridTypeSelectorData.shouldDisplay) return;
    return (
      <div>
        <FormFieldTitle>{gridTypeSelectorData.title}</FormFieldTitle>
        <Select
          value={gridTypeSelectorData.value}
          onChange={gridTypeSelectorData.onChange}
          placeholder={gridTypeSelectorData.placeholder}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          style={{ width: "100%" }}
          options={gridTypeSelectorData.options.map((option) => ({
            value: option,
            label: translateOptionName(option),
          }))}
          mobileTitle={gridTypeSelectorData.title}
        />
      </div>
    );
  };

  const getNumGridsSelector = () => {
    return (
      <div>
        <FormFieldTitle>{t("labels.grids")}</FormFieldTitle>
        <NumGridsInput
          onChange={numGridsSelectorData.onChange}
          value={numGridsSelectorData.value}
          suffix={numGridsSelectorData.suffix}
          placeholder={numGridsSelectorData.placeholder}
          stringMode
          useDebounce={true}
        />
      </div>
    );
  };

  const getGridsAndTypeSelector = () => {
    return (
      <NumGridsAndTypeWrapper $isStacked={isHedgingRangeLayout}>
        <div style={{ display: "flex", columnGap: 16 }}>
          <div style={{ flex: 1 }}>{getNumGridsSelector()}</div>
          {/* New look: without the grid type, Grids takes the full row like
              the fields around it. */}
          {(!isNewLook || gridTypeSelectorData.shouldDisplay) && (
            <div style={{ flex: 1 }}>{getGridTypeSelector()}</div>
          )}
        </div>
        {numGridsError && <ErrorText>{numGridsError}</ErrorText>}
        {generalGridsError && isGeneralTab && (
          <ErrorText>{generalGridsError}</ErrorText>
        )}
      </NumGridsAndTypeWrapper>
    );
  };

  const isHedgingRangeLayout = !!hedgingExposureSelectorData;
  const isHedgingExposureInline =
    hedgingExposureWidth >= HEDGING_EXPOSURE_INLINE_MIN_WIDTH;

  const getRangeSettingsContent = () => {
    const content = (
      <>
        <PriceRangeSection $isStacked={isHedgingRangeLayout}>
          {getPriceRangeSelector()}
          {priceErrorMessage && <ErrorText>{priceErrorMessage}</ErrorText>}
        </PriceRangeSection>
        {isHedgingRangeLayout && getGridsAndTypeSelector()}
        {isHedgingRangeLayout && afterPriceRangeContent && (
          <AfterPriceRangeContent>
            {afterPriceRangeContent}
          </AfterPriceRangeContent>
        )}
      </>
    );
    if (!isHedgingRangeLayout) {
      return content;
    }
    return (
      <TitleWithToggle
        content={content}
        hideWithOpacity={true}
        hideToggle={!rangeStrategySectionOverrideData?.isMultiValue}
        showMultipleValues={!!rangeStrategySectionOverrideData?.isMultiValue}
        overrideObjectName={t("labels.hedging_strategy")}
        disableOverrideButton={rangeStrategySectionOverrideData?.disabled}
        onDisabledOverrideButtonClick={() =>
          openMessage(HEDGING_RANGE_SYMBOL_OVERRIDE_WARNING, "warning")
        }
        onOverrideChange={rangeStrategySectionOverrideData?.onOverride}
        overrideButtonWrapperTestId={HEDGING_STRATEGY_OVERRIDE_BUTTON_ID}
      />
    );
  };

  return (
    <Wrapper style={style} $isStacked={isHedgingRangeLayout}>
      {getRangeSettingsContent()}
      {getHedgingExposureSelector()}
      {!isHedgingRangeLayout && getGridsAndTypeSelector()}
    </Wrapper>
  );
};

export default GridsSelector;

const HEDGING_EXPOSURE_INPUT_MIN_WIDTH = 275;
const HEDGING_EXPOSURE_INPUT_GAP = 16;
const HEDGING_EXPOSURE_INLINE_MIN_WIDTH =
  HEDGING_EXPOSURE_INPUT_MIN_WIDTH * 2 + HEDGING_EXPOSURE_INPUT_GAP;

const Wrapper = styled.div<{ $isStacked: boolean }>`
  display: flex;
  align-items: ${(p) => (p.$isStacked ? "stretch" : "center")};
  column-gap: 16px;
  row-gap: 16px;
  flex-wrap: wrap;
`;
const PriceRangeSection = styled.div<{ $isStacked: boolean }>`
  flex: 1;
  min-width: 230px;
  width: ${(p) => (p.$isStacked ? "100%" : "auto")};
`;
const NumGridsAndTypeWrapper = styled.div<{ $isStacked: boolean }>`
  width: 100%;
  flex: 1 0 auto;
  margin-top: ${(p) => (p.$isStacked ? "16px" : 0)};
`;
const AfterPriceRangeContent = styled.div`
  width: 100%;
  margin-top: 16px;
`;
const SectionDivider = styled.div`
  width: 100%;
  border-top: ${(p) => p.theme.borders.light};
  opacity: 0.85;
`;
const HedgingExposureSection = styled.div`
  width: 100%;
  min-width: 0;
`;
const HedgingExposureWrapper = styled(Flex)<{ $isInline: boolean }>`
  display: ${(p) => (p.$isInline ? "grid" : "flex")};
  flex-direction: column;
  grid-template-columns: ${(p) =>
    p.$isInline ? "minmax(0, 1fr) minmax(0, 1fr)" : "none"};
  column-gap: ${HEDGING_EXPOSURE_INPUT_GAP}px;
  row-gap: 18px;
  margin-top: 20px;
  min-width: 0;

  ${SectionDivider} {
    display: ${(p) => (p.$isInline ? "none" : "block")};
  }
`;
const HedgingExposureInput = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
`;
const PriceInputsWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 16px;
  row-gap: 12px;

  @media (max-width: 390px) {
    grid-template-columns: 1fr;
  }
`;
const PriceInputWrapper = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
`;
const SectionLabel = styled(Text).attrs({ type: "secondary" })`
  display: block;
  margin-bottom: 10px;
  font-size: ${typography.fontSizes.S};
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  ${(p) =>
    p.theme.isNewLook &&
    css`
      &&& {
        margin-bottom: 8px;
        ${newLookType.micro};
        color: ${getNewLookTokens(p.theme).muted};
      }
    `}
`;
const NumGridsInput = styled(CustomInputNumber)`
  .ant-input-suffix {
    color: ${(p) => p.theme.colors.secondaryGrey};
    opacity: 0.9;
  }
`;
